import { CountryInterface } from "../../interfaces/shared/CountryInterface";
import { DealerInterface } from "../../interfaces/shared/DealerInterface";
import { LanguageInterface } from "../../interfaces/shared/LanguageInterface";
import { UserInterface } from "../../interfaces/user/UserInterface";
import { DeliveryAddressInterface } from "../../interfaces/user/DeliveryAddressInterface";

interface V2LanguageData {
    id: number;
    name: string;
    locale_code: string;
}

interface V2CountryData {
    id: number;
    name: string;
}

interface V2DealerAddress {
    street?: string | null;
    postal_code?: string | null;
    state?: string | null;
    city?: string | null;
    country?: string | null;
}

interface V2DealerData {
    id: number;
    name: string;
    website?: string | null;
    phone_number?: string | null;
    email?: string | null;
    logo?: string | null;
    address?: V2DealerAddress | null;
}

export interface V2DealerUserResponse {
    data: {
        id: number;
        dealer: V2DealerData;
        active_localization: {
            language: V2LanguageData;
            country: V2CountryData;
        };
        available_localization: {
            languages: V2LanguageData[];
            countries: V2CountryData[];
        };
        salutation?: string | null;
        first_name: string;
        insertion?: string | null;
        last_name: string;
        function?: string | null;
        email?: string | null;
        phone_number?: string | null;
        communication_email?: string | null;
        roles?: string[] | null;
        refurbished_visited_at?: string | null;
        refurbished_shop_enabled: boolean;
        delivery_address: DeliveryAddressInterface;
    };
}

const mapLanguage = (language: V2LanguageData): LanguageInterface => ({
    id: language.id,
    localeCode: language.locale_code,
    name: language.name,
});

const mapCountry = (country: V2CountryData): CountryInterface => ({
    id: country.id,
    name: country.name,
});

const mapDealer = (dealer: V2DealerData): DealerInterface => ({
    id: dealer.id,
    email: dealer.email ?? "",
    name: dealer.name,
    phoneNumber: dealer.phone_number ?? "",
    logo: dealer.logo ?? "",
    website: dealer.website ?? "",
    address: {
        street: dealer.address?.street ?? "",
        postal_code: dealer.address?.postal_code ?? "",
        state: dealer.address?.state ?? "",
        city: dealer.address?.city ?? "",
        country: dealer.address?.country ?? "",
    },
});

export const mapDealerUserResponse = (
    response: V2DealerUserResponse,
): UserInterface => {
    const user = response.data;

    return {
        id: user.id,
        activeLanguage: mapLanguage(user.active_localization.language),
        activeCountry: mapCountry(user.active_localization.country),
        communicationEmail: user.communication_email ?? undefined,
        languages: user.available_localization.languages.map(mapLanguage),
        countries: user.available_localization.countries.map(mapCountry),
        dealer: mapDealer(user.dealer),
        salutation: user.salutation ?? "",
        firstName: user.first_name,
        insertion: user.insertion ?? "",
        lastName: user.last_name,
        function: user.function ?? "",
        email: user.email ?? "",
        phoneNumber: user.phone_number ?? "",
        roles: user.roles ?? [],
        refurbishedVisitedAt: user.refurbished_visited_at ?? null,
        refurbishedShopEnabled: user.refurbished_shop_enabled,
        delivery_address: user.delivery_address,
    };
};
